import {useCallback, useEffect, useState} from 'react'
import {type MatrixEvent} from 'matrix-js-sdk'

import {useMatrixContext} from '#/state/matrix'

const CALL_EVENT_TYPE = 'com.batesky.call'
const STALE_TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes

type CallContent = {
  status: 'active' | 'inactive'
  callId: string
  startedBy: string
  startedAt: number
}

export function useCallState(roomId: string) {
  const {client, isConnected} = useMatrixContext()
  const myUserId = client?.getUserId() || ''

  const [callContent, setCallContent] = useState<CallContent | null>(null)

  const readCallState = useCallback(() => {
    if (!client) return
    const room = client.getRoom(roomId)
    if (!room) return

    const event = room.currentState.getStateEvents(CALL_EVENT_TYPE as any, '')
    if (!event) {
      setCallContent(null)
      return
    }

    const content = event.getContent() as CallContent | undefined
    if (!content || content.status !== 'active') {
      setCallContent(null)
      return
    }

    // Stale detection: treat calls older than 10 minutes as inactive
    if (Date.now() - content.startedAt > STALE_TIMEOUT_MS) {
      setCallContent(null)
      return
    }

    setCallContent(content)
  }, [client, roomId])

  useEffect(() => {
    if (!client || !isConnected) return

    readCallState()

    const onStateEvent = (event: MatrixEvent) => {
      if (event.getRoomId() === roomId && event.getType() === CALL_EVENT_TYPE) {
        readCallState()
      }
    }

    client.on('RoomState.events' as any, onStateEvent)
    return () => {
      client.removeListener('RoomState.events' as any, onStateEvent)
    }
  }, [client, isConnected, roomId, readCallState])

  const isCallActive = !!callContent
  const isStartedByMe = callContent?.startedBy === myUserId
  const callId = callContent?.callId ?? null

  const startCall = useCallback(
    async (newCallId: string) => {
      if (!client) return
      await (client as any).sendStateEvent(
        roomId,
        CALL_EVENT_TYPE,
        {
          status: 'active',
          callId: newCallId,
          startedBy: myUserId,
          startedAt: Date.now(),
        } satisfies CallContent,
        '',
      )
    },
    [client, roomId, myUserId],
  )

  const endCall = useCallback(async () => {
    if (!client) return
    await (client as any).sendStateEvent(
      roomId,
      CALL_EVENT_TYPE,
      {status: 'inactive'} as Partial<CallContent>,
      '',
    )
  }, [client, roomId])

  return {isCallActive, isStartedByMe, callId, startCall, endCall}
}
